import { Box, Text, ThemeSpaceVariable, ThemeColorVariable } from "@caspeco/casper-ui-library";
import { SafeHTML } from "components/safe-html";
import { cssVarToRgba } from "components/show-more-text/show-more-text-helper";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
interface IShowMoreText {
    text?: string;
    children?: ReactNode;
    initialLines?: number;
    showMoreText?: string;
    showLessText?: string;
    textProps?: React.ComponentProps<typeof Text>;
    linkProps?: React.ComponentProps<typeof Text>;
    backgroundColor?: ThemeColorVariable;
    onToggle?: (isExpanded: boolean) => void;
    /**
     * Clamp the content in a Box rather than a Text. Casper's Text only renders inline elements
     * and `<p>`, so block-level children — the paragraphs, lists and headings RichTextRenderer
     * emits — cannot legally nest inside it. Set this when passing rich content as children;
     * `textProps` does not apply in that mode.
     */
    blockContent?: boolean;
}

const ShowMoreText = ({
    text,
    children,
    initialLines,
    showMoreText,
    showLessText,
    textProps = {},
    linkProps = {},
    backgroundColor = ThemeColorVariable.Surface,
    onToggle,
    blockContent = false,
}: IShowMoreText) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const showMoreRef = useRef<HTMLDivElement>(null);
    const [gradiantWidth, setGradiantWidth] = useState(0);
    const { t } = useTranslation();
    // Create a unique ID for the text element to avoid conflicts with other elements on the page
    const textId = `show-more-text-${Math.random().toString(36).substring(2, 9)}`;

    useEffect(() => {
        const element = document.getElementById(textId);
        if (!element) return;

        const checkTruncation = () => {
            const element = document.getElementById(textId);
            if (!element) return;

            if (isExpanded) return;

            const originalLineClamp = element.style.webkitLineClamp;
            const truncatedHeight = element.clientHeight;

            element.style.webkitLineClamp = "none";
            const fullHeight = element.clientHeight;
            element.style.webkitLineClamp = originalLineClamp;

            const wasTruncated = fullHeight > truncatedHeight;
            setIsTruncated(wasTruncated);
        };

        // Initial check after render
        const timer = setTimeout(checkTruncation, 0);

        setGradiantWidth(
            showMoreRef.current?.clientWidth ? showMoreRef.current.clientWidth * 2.1 : 0
        );
        // Check on resize
        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(element);

        return () => {
            clearTimeout(timer);
            resizeObserver.disconnect();
        };
    }, [text, initialLines, textId, isExpanded]);

    if (!text && !children) return null;

    const defaultShowMoreText = showMoreText || t("showMoreText.showMore");
    const defaultShowLessText = showLessText || t("showMoreText.showLess");

    const showButton = isTruncated;

    const ShowMoreButton = () => (
        <div
            onClick={() => {
                setIsExpanded(!isExpanded);
                onToggle?.(!isExpanded);
            }}
            aria-hidden="true"
            ref={showMoreRef}
            data-track-click={isExpanded ? "show-less-text" : "show-more-text"}
        >
            <Text fontWeight="400" textDecoration="underline" cursor="pointer" {...linkProps}>
                {isExpanded ? defaultShowLessText : defaultShowMoreText}
            </Text>
        </div>
    );

    const rgba = cssVarToRgba(backgroundColor, 0);
    const rgbaOne = cssVarToRgba(backgroundColor, 1);
    const backgroundLinear = `linear-gradient(90deg, ${rgba} 0%, ${rgbaOne} 40%, ${rgbaOne} 100%)`;
    return (
        <Box position="relative" display="flex" flexDirection="column">
            {blockContent ? (
                <Box
                    id={textId}
                    wordBreak="break-word"
                    overflowWrap="anywhere"
                    // Casper's Box has no noOfLines, so the clamp is spelled out here. Text does
                    // have it, but Text renders only inline elements and <p>, which block-level
                    // rich content cannot legally nest inside. Written as an inline style so that
                    // checkTruncation's read/restore of element.style.webkitLineClamp still works.
                    style={
                        isExpanded || !initialLines
                            ? undefined
                            : {
                                  display: "-webkit-box",
                                  WebkitBoxOrient: "vertical",
                                  WebkitLineClamp: initialLines,
                                  overflow: "hidden",
                              }
                    }
                >
                    {children ?? <SafeHTML html={text!} />}
                </Box>
            ) : (
                <Text
                    id={textId}
                    noOfLines={isExpanded ? undefined : initialLines}
                    wordBreak="break-word"
                    overflowWrap="anywhere"
                    {...textProps}
                >
                    {children ?? <SafeHTML html={text!} />}
                </Text>
            )}

            {showButton && !isExpanded && (
                <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    height={`${showMoreRef.current?.clientHeight}px`}
                    width={`${gradiantWidth}px`}
                    bg={backgroundLinear}
                    zIndex="1"
                    pointerEvents="none"
                />
            )}

            {showButton && !isExpanded && (
                <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    height={`${showMoreRef.current?.clientHeight}px`}
                    paddingLeft={ThemeSpaceVariable.Small}
                    paddingRight={ThemeSpaceVariable.Small}
                    display="flex"
                    alignItems="center"
                    zIndex="2"
                >
                    <ShowMoreButton />
                </Box>
            )}
            {isExpanded && <ShowMoreButton />}
        </Box>
    );
};

export default ShowMoreText;
